import { BufferGeometry, Float32BufferAttribute, Mesh, Vector3 } from 'three';
import { SDFGenerator, VoxelGrid, getSdfWorkerPool } from '@modules/three/common/voxels';

/**
 * The inner wall of a hollow print, the way every other tool builds it: as an
 * ISO-SURFACE OF A DISTANCE FIELD, not by pushing each vertex along its own
 * normal.
 *
 * The set of points exactly `t` inside a solid is a level set of its signed
 * distance function, and a level set has the properties we were otherwise
 * chasing with post-effects. It cannot self-intersect — no fold repair. It is
 * never outside the solid, by definition — no escape repair. Where the solid
 * is thinner than 2t it simply DOES NOT EXIST, and the two opposing walls meet
 * at the medial axis, which is the honest "this region prints solid" — no
 * cavity rescue. And it is as smooth as the field, so neighbouring vertices
 * cannot disagree — no step smoothing. That is the whole reason PrusaSlicer,
 * Chitubox, Meshmixer and nTopology all hollow this way and expose two knobs
 * (wall thickness, voxel size) instead of a dozen repair thresholds.
 *
 * What we do NOT copy from them is running marching cubes over the field and
 * keeping its output: that resamples the OUTER surface too, and a resampled
 * scan turns fine anatomy into printed ridges (measured, and the reason the
 * direct offset exists at all). Instead the field is sampled onto the outer
 * mesh's OWN topology — each outer vertex is projected onto the iso-surface at
 * −t by Newton steps on the field. The outer surface therefore stays
 * bit-identical to the fused sheet, the rim bridging stays the trivial 1:1
 * pairing it always was, and the inner wall is still a genuine level set.
 *
 * The field is discrete, so the sampled iso is smooth at the grid's scale
 * rather than exactly — which is the point. Fidelity on the inside is worth
 * nothing: no foil ever touches it.
 */

/**
 * Signed distance to the solid: negative inside, positive outside, in mm.
 * Abstracted so `buildOffsetShell` stays free of voxel imports, and so tests
 * can drive it with an analytic field whose answer is known exactly.
 */
export type DistanceField = {
  /** Signed distance at `p`, mm. */
  sample(p: Vector3): number;
  /** Unit-ish gradient at `p` (points away from the solid), written into `out`. */
  gradient(p: Vector3, out: Vector3): Vector3;
  /** The scale the field actually resolves, mm — a sensible marching step. */
  resolutionMm?: number;
};

export type InsideFieldOptions = {
  /** Wall thickness the field has to reach, mm — sets the band width. */
  thicknessMm: number;
  /** Which way the model's open base faces, so the plug below it goes the right way. */
  opens: 'up' | 'down';
  /** Grid resolution, mm. Defaults to a quarter of the wall, floored at 0.2. */
  cellMm?: number;
  /**
   * Where the open base actually is. Defaults to the bounding box's extreme in
   * `opens`, which is right whenever the rim is the model's last thing in that
   * direction — but a caller that KNOWS the plane (the skirt returns it) should
   * say so, because the plug has to seal exactly there.
   */
  planeY?: number;
};

export type InsideGridResult = {
  grid: VoxelGrid;
  cellMm: number;
  bandMm: number;
};

/**
 * The raw narrow-band grid behind `buildInsideDistanceField`, for callers that
 * want the field itself rather than a sampler over it — marching cubes on the
 * −t iso needs the cells.
 */
export async function buildInsideVoxelGrid(
  sheet: BufferGeometry,
  options: InsideFieldOptions,
): Promise<InsideGridResult> {
  const thickness = options.thicknessMm;
  const cell = options.cellMm ?? Math.max(0.2, thickness / 4);
  // Enough band to hold the whole offset plus room for the gradient stencil.
  const band = thickness + 6 * cell;

  sheet.computeBoundingBox();
  const box = sheet.boundingBox!;
  const plugTop = options.planeY ?? (options.opens === 'up' ? box.max.y : box.min.y);
  const plug = plugBox(box.min.x, box.max.x, box.min.z, box.max.z, plugTop, options.opens, cell, thickness);

  const generator = new SDFGenerator([new Mesh(sheet), new Mesh(plug)]);
  const grid = await generator.generateNarrowBandFlood(
    {
      cellSize: cell,
      padding: Math.max(1, thickness),
      narrowBandMm: band,
      // The plug closed the base, but seams elsewhere can still leave hairline
      // slits; the light seal handles those without the volume escalation.
      openSheet: true,
    },
    getSdfWorkerPool(),
  );
  generator.dispose();
  plug.dispose();

  return { grid, cellMm: cell, bandMm: band };
}

export type InsideFieldResult = {
  field: DistanceField;
  cellMm: number;
  bandMm: number;
  /** Grid cell count, for the log — this is the memory the pass costs. */
  cells: number;
};

/**
 * A signed distance field of the SOLID the sheet bounds.
 *
 * The sheet is open at the base (that is what lets resin drain), and an open
 * solid has no inside: the sign flood would pour straight up through the base
 * and declare the whole jaw interior "outside". So a PLUG — a closed box just
 * below the base plane, overlapping it by a couple of cells — is unioned in
 * for the field only. It closes the volume for the flood, and because the
 * union takes the minimum signed distance it cannot corrupt the field anywhere
 * the sheet itself is nearer, which is everywhere above the base.
 */
export async function buildInsideDistanceField(
  sheet: BufferGeometry,
  options: InsideFieldOptions,
): Promise<InsideFieldResult> {
  const { grid, cellMm, bandMm } = await buildInsideVoxelGrid(sheet, options);
  return {
    field: voxelDistanceField(grid, bandMm),
    cellMm,
    bandMm,
    cells: grid.info.total,
  };
}

/** Trilinear sampling of a voxel grid, with central-difference gradients. */
export function voxelDistanceField(grid: VoxelGrid, band: number): DistanceField {
  const { origin, cellSize, counts, strides } = grid.info;
  const data = grid.data;
  const maxX = counts.x - 1;
  const maxY = counts.y - 1;
  const maxZ = counts.z - 1;

  const sampleAt = (px: number, py: number, pz: number): number => {
    let gx = (px - origin.x) / cellSize;
    let gy = (py - origin.y) / cellSize;
    let gz = (pz - origin.z) / cellSize;
    // Outside the grid there is nothing but air, and the band sentinel says so.
    if (gx < 0 || gy < 0 || gz < 0 || gx > maxX || gy > maxY || gz > maxZ) return band;
    gx = Math.min(gx, maxX - 1e-6);
    gy = Math.min(gy, maxY - 1e-6);
    gz = Math.min(gz, maxZ - 1e-6);
    const x0 = Math.floor(gx);
    const y0 = Math.floor(gy);
    const z0 = Math.floor(gz);
    const fx = gx - x0;
    const fy = gy - y0;
    const fz = gz - z0;
    const base = x0 * strides.x + y0 * strides.y + z0 * strides.z;
    const c000 = data[base];
    const c100 = data[base + strides.x];
    const c010 = data[base + strides.y];
    const c110 = data[base + strides.x + strides.y];
    const c001 = data[base + strides.z];
    const c101 = data[base + strides.x + strides.z];
    const c011 = data[base + strides.y + strides.z];
    const c111 = data[base + strides.x + strides.y + strides.z];
    const c00 = c000 + (c100 - c000) * fx;
    const c10 = c010 + (c110 - c010) * fx;
    const c01 = c001 + (c101 - c001) * fx;
    const c11 = c011 + (c111 - c011) * fx;
    const c0 = c00 + (c10 - c00) * fy;
    const c1 = c01 + (c11 - c01) * fy;
    return c0 + (c1 - c0) * fz;
  };

  const h = cellSize;
  return {
    resolutionMm: cellSize,
    sample: (p) => sampleAt(p.x, p.y, p.z),
    gradient: (p, out) =>
      out
        .set(
          sampleAt(p.x + h, p.y, p.z) - sampleAt(p.x - h, p.y, p.z),
          sampleAt(p.x, p.y + h, p.z) - sampleAt(p.x, p.y - h, p.z),
          sampleAt(p.x, p.y, p.z + h) - sampleAt(p.x, p.y, p.z - h),
        )
        .multiplyScalar(1 / (2 * h)),
  };
}

export type IsoProjection = {
  /** True when the iso-surface was actually reached. */
  converged: boolean;
  /** Signed distance the point ended at — how deep the material let it go. */
  reached: number;
};

/**
 * Walks `point` onto the iso-surface `iso` (a negative number: `−thickness`)
 * by Newton steps. The field is a distance function, so its gradient has unit
 * length and the step is simply `−(d − iso)·∇d` — it converges in a handful of
 * iterations.
 *
 * Where the material is thinner than 2·thickness the iso-surface does not
 * exist locally: the field's minimum along the way is above `iso`, the
 * iteration stalls at the deepest point it can find — the medial axis — and
 * `converged` comes back false. That is not a failure. It is the correct
 * answer, and it is what makes those regions print solid without a rescue
 * pass: both walls' vertices land on the same medial surface and the cavity
 * between them closes itself.
 */
export function projectOntoIso(
  field: DistanceField,
  point: Vector3,
  iso: number,
  maxSteps = 12,
  tolerance = 1e-3,
): IsoProjection {
  const gradient = new Vector3();
  const trial = new Vector3();
  const best = point.clone();
  let distance = field.sample(point);
  let bestError = Math.abs(distance - iso);
  let bestDistance = distance;
  for (let step = 0; step < maxSteps; step++) {
    const error = distance - iso;
    if (Math.abs(error) <= tolerance) return { converged: true, reached: distance };
    field.gradient(point, gradient);
    const lengthSq = gradient.lengthSq();
    if (lengthSq < 1e-12) break;

    // Newton along the gradient — with BACKTRACKING, because a distance field
    // has a kink at its medial axis and the full step there jumps clean across
    // to the far wall and oscillates for ever. Halving until the step actually
    // improves converges onto the kink instead, which is precisely the answer
    // wanted in a region too thin to hollow.
    let damping = 1;
    let moved = false;
    for (let attempt = 0; attempt < 6; attempt++) {
      trial.copy(point).addScaledVector(gradient, (-error / lengthSq) * damping);
      const trialDistance = field.sample(trial);
      if (Math.abs(trialDistance - iso) < Math.abs(error)) {
        point.copy(trial);
        distance = trialDistance;
        moved = true;
        break;
      }
      damping *= 0.5;
    }
    if (!moved) break;
    if (Math.abs(distance - iso) < bestError) {
      bestError = Math.abs(distance - iso);
      bestDistance = distance;
      best.copy(point);
    }
  }
  if (Math.abs(distance - iso) <= tolerance) return { converged: true, reached: distance };
  // Stalled: hand back the deepest point the walk actually found.
  point.copy(best);
  return { converged: false, reached: bestDistance };
}

export type RayDepth = {
  /** How far along the ray the iso-surface sits, mm. */
  depth: number;
  /** False when the ray never got that deep — the material is too thin. */
  reached: boolean;
  /**
   * Whether the ray was ever inside the solid at all. False means the field
   * disagrees that there is material behind this vertex — a face the manifold
   * repair left wound the wrong way, or a fill patch stretched across a void —
   * and `depth` is then meaningless, not merely short.
   */
  entered: boolean;
};

/**
 * How deep the wall can go at one vertex: the first crossing of `iso` along
 * the ray, found by marching and then bisecting.
 *
 * This is the form the shell actually uses, in preference to a free 3D
 * projection onto the iso-surface. Both put the vertex ON the level set, but a
 * free projection also lets it slide SIDEWAYS across it, and since the
 * iso-surface has less area than the outer one wherever the crown is convex,
 * neighbouring vertices bunch up and change places — measured on the captured
 * jaw, 5164 of 107580 inner faces came out folded. Constrained to its own
 * normal, a vertex cannot overtake its neighbours: the inner surface is the
 * outer one moved inward by a distance the FIELD chooses, which is exactly
 * what "the inside is the outside, shrunk" means.
 *
 * Where the ray never reaches `iso` the material is thinner than two walls;
 * the deepest point found is returned instead, and `reached` is false. That
 * point is on the medial axis, so the opposing wall stops there too and the
 * region closes into solid on its own.
 */
export function depthToIso(
  field: DistanceField,
  origin: Vector3,
  direction: Vector3,
  iso: number,
  maxDepth: number,
  stepMm: number,
): RayDepth {
  const probe = new Vector3();
  const sampleAt = (s: number) => field.sample(probe.copy(origin).addScaledVector(direction, s));
  let previous = 0;
  let bestDepth = 0;
  let bestDistance = sampleAt(0);
  let entered = false;
  for (let s = stepMm; s <= maxDepth + 1e-9; s += stepMm) {
    const distance = sampleAt(s);
    if (distance <= iso) {
      let low = previous;
      let high = s;
      for (let i = 0; i < 16; i++) {
        const mid = (low + high) / 2;
        if (sampleAt(mid) <= iso) high = mid;
        else low = mid;
      }
      return { depth: high, reached: true, entered: true };
    }
    if (distance < bestDistance) {
      bestDistance = distance;
      bestDepth = s;
    }
    // Stop at the far side of THIS piece of material. Without it a ray that
    // crosses a thin interproximal wall carries on through the gap, enters the
    // neighbouring crown and reports its depth — putting the vertex inside a
    // different tooth, which reads back as the wall poking out of the model.
    if (entered && distance >= -1e-6) break;
    if (distance < -1e-6) entered = true;
    previous = s;
  }
  return { depth: bestDepth, reached: false, entered };
}

/** A closed box sealing the model's open base, for the sign flood only. */
function plugBox(
  minX: number,
  maxX: number,
  minZ: number,
  maxZ: number,
  plane: number,
  opens: 'up' | 'down',
  cell: number,
  thickness: number,
): BufferGeometry {
  const pad = Math.max(1, thickness);
  const x0 = minX - pad;
  const x1 = maxX + pad;
  const z0 = minZ - pad;
  const z1 = maxZ + pad;
  // Overlaps the sheet's rim by two cells so no hairline gap survives the
  // rasterisation, and reaches well past it on the outside.
  const inner = opens === 'up' ? plane - 2 * cell : plane + 2 * cell;
  const outer = opens === 'up' ? plane + 2 * pad : plane - 2 * pad;
  const y0 = Math.min(inner, outer);
  const y1 = Math.max(inner, outer);

  const corners = [
    [x0, y0, z0],
    [x1, y0, z0],
    [x1, y0, z1],
    [x0, y0, z1],
    [x0, y1, z0],
    [x1, y1, z0],
    [x1, y1, z1],
    [x0, y1, z1],
  ].flat();
  // Outward-wound faces: bottom, top, and the four sides. Checked by the cross
  // product on every face, not by eye — the first version of this table was
  // inside-out on all six, which a sign FLOOD forgives (it only needs the box
  // to be watertight) right up until something reads the field as geometry.
  const faces = [
    0, 1, 2, 0, 2, 3, // -Y
    4, 6, 5, 4, 7, 6, // +Y
    0, 5, 1, 0, 4, 5, // -Z
    1, 6, 2, 1, 5, 6, // +X
    2, 7, 3, 2, 6, 7, // +Z
    3, 4, 0, 3, 7, 4, // -X
  ];
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(corners, 3));
  geometry.setIndex(faces);
  return geometry;
}
